import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import axios from 'axios';
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { APIURL } from "../config";
import './registerpage.css'
import Snackbar from './Snackbar';

function PasswordReset() {
    const [token, setToken] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [enableSubmit, setEnableSubmit] = useState(true);
    const [buttonText, setButtonText] = useState("Submit");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "info" });
    const toggleShowPassword = () => setShowPassword(!showPassword);
    const toggleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);
    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));


    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        setToken(queryParams.get('token') || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRegistration = async () => {
        setPasswordError("");
        setConfirmPasswordError("");
        setError("");
        setButtonText("Submitting...");
        setEnableSubmit(false);

        let isValid = true;

        // Trim leading/trailing whitespace (common copy-paste artifact from password managers)
        const trimmedPassword = password ? password.trim() : '';
        if (trimmedPassword !== password) {
            setPassword(trimmedPassword); // update the field so user sees the trimmed value
        }

        if (!trimmedPassword) {
            setPasswordError("Password is required.");
            isValid = false;
        } else if (/[^\x20-\x7E]/.test(trimmedPassword)) {
            // Reject non-ASCII characters (é, ñ, etc.) — bcrypt has a 72-byte limit and multi-byte chars cause inconsistent hashing
            setPasswordError("Password can only contain English letters, numbers, spaces, and standard special characters.");
            isValid = false;
        } else {
            // Password Strength Validation (must match server-side policy: min 10 chars, lowercase, uppercase, digit, special)
            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{10,}$/;

            if (!passwordRegex.test(trimmedPassword)) {
                setPasswordError("Password does not meet the requirements below.");
                isValid = false;
            }
        }

        if (!confirmPassword) {
            setConfirmPasswordError("Please confirm your password.");
            isValid = false;
        } else if (trimmedPassword !== confirmPassword.trim()) {
            setConfirmPasswordError("Passwords do not match.");
            isValid = false;
        }
        if (!isValid) {
            setError("Please fill in all fields correctly.");
            setButtonText("Submit");
            setEnableSubmit(true);
            return;
        }

        try {
            const response = await axios.post(APIURL + '/reset-password', {
                newPassword: password,
                token: token
            });

            if (response.status === 403)
                setError("Password reset link has expired. Please request a new one.");
            else if (response.status === 200) {
                setSnackbar({ open: true, message: "Password reset successful! Redirecting to login...", type: "success" });
                setButtonText("Success!");
                setTimeout(() => navigate("/login"), 2000);
                return;
            } else {
                setSnackbar({ open: true, message: "An error occurred. Please try again.", type: "error" });
                setError("An error occurred. Please try again.");
            }
        } catch (err) {
            if (err.response && err.response.status === 403) {
                setSnackbar({ open: true, message: "Password reset link has expired. Please request a new one.", type: "error" });
                setError("Password reset link has expired. Please request a new one.");
            } else {
                setSnackbar({ open: true, message: "An error occurred while resetting your password. Please try again.", type: "error" });
                setError("An error occurred while resetting your password. Please try again.");
            }
        }
        setButtonText("Submit");
        setEnableSubmit(true);
    }

    return (
        <div>
            <Snackbar
                message={snackbar.message}
                type={snackbar.type}
                open={snackbar.open}
                onClose={closeSnackbar}
            />
            <div className="loginsignup">
                <div className="loginsignup-container">
                    <h1>Reset Password</h1>
                    <div className="loginsignup-fields">
                        <label>
                            Password
                            <br />

                            <div className="password-container">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="password-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                />
                                <span style={{
                                    position: 'absolute',
                                    right: '10px',
                                    cursor: 'pointer',
                                    color: "#6c6c7d"
                                }} className="eye-button" onClick={toggleShowPassword}>
                                    {
                                        showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />
                                    }              </span>
                            </div>
                            <span style={{ color: "red" }}>{passwordError}</span>

                            {/* Password Policy Requirements */}
                            <div style={{ marginTop: '8px', fontSize: '13px', lineHeight: '1.6' }}>
                                <p style={{ margin: 0, fontWeight: '600', color: '#555' }}>Password must contain:</p>
                                <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0 0 0' }}>
                                    <li style={{ color: !/[^\x20-\x7E]/.test(password) ? 'green' : 'red' }}>
                                        {!/[^\x20-\x7E]/.test(password) ? '\u2713' : '\u2717'} Only English letters, numbers, and standard symbols
                                    </li>
                                    <li style={{ color: password.trim().length >= 10 ? 'green' : '#888' }}>
                                        {password.trim().length >= 10 ? '\u2713' : '\u2717'} At least 10 characters
                                    </li>
                                    <li style={{ color: /[a-z]/.test(password) ? 'green' : '#888' }}>
                                        {/[a-z]/.test(password) ? '\u2713' : '\u2717'} One lowercase letter (a-z)
                                    </li>
                                    <li style={{ color: /[A-Z]/.test(password) ? 'green' : '#888' }}>
                                        {/[A-Z]/.test(password) ? '\u2713' : '\u2717'} One uppercase letter (A-Z)
                                    </li>
                                    <li style={{ color: /\d/.test(password) ? 'green' : '#888' }}>
                                        {/\d/.test(password) ? '\u2713' : '\u2717'} One numeric digit (0-9)
                                    </li>
                                    <li style={{ color: /[@$!%*?&]/.test(password) ? 'green' : '#888' }}>
                                        {/[@$!%*?&]/.test(password) ? '\u2713' : '\u2717'} One special character (@$!%*?&)
                                    </li>
                                </ul>
                            </div>
                        </label>
                        <br />
                        <label>
                            Confirm Password
                            <br />
                            <div className="password-container">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="password-input"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Enter confirm password"
                                />
                                <span style={{
                                    position: 'absolute',
                                    right: '10px',
                                    cursor: 'pointer',
                                    color: "#6c6c7d"
                                }} className="eye-button" onClick={toggleShowConfirmPassword}>
                                    {
                                        showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />
                                    }              </span>
                            </div>
                            <span style={{ color: "red" }}>{confirmPasswordError}</span>
                        </label>
                    </div>
                    <br />
                    <button className={`submit-button ${!enableSubmit ? "btn-loading" : ""}`} onClick={handleRegistration} disabled={!enableSubmit}>
                        {!enableSubmit && <span className="btn-spinner"></span>}
                        {buttonText || "Submit"}
                    </button>
                    {error && <p style={{ color: "red" }}>{error}</p>}
                    <br />
                    <br />

                    <button
                        type="button"
                        style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
                        className="register"
                        onClick={() => navigate("/login")}
                    >
                        Sign-in here
                    </button>
                    <br />
                    <br />
                </div>
            </div >
        </div >
    )
}

export default PasswordReset